// ルート編集（マーク列の編集）のユーティリティ。
//
// 記録ルートは機体側 (mark_trajectory_generator.cpp) で「マーク列」から生成される:
// - マーク i と i+1 の間にステーション rec-{i}-{i+1} / rec-{i+1}-{i} と
//   直線ガイドラインが作られる（両ステーションの theta は経路方向で上書き。
//   区間を後進で走行する場合は +π）。
// - スロットは先頭マーク (rec-1) と末尾マーク (rec-2) の 2 つだけが作られ、
//   height / type（荷役=1）が永続化される。中間マークの荷役情報は残らない。
//
// したがって getMap のステーション群からマーク列（位置・前進/後進・端点の
// 荷役設定）を完全に復元でき、mark_at_poses → process で同じルートを再生成できる。

import type { EditableMark, RouteMap } from '@/types'
import { ENABLE_CARGO_TOGGLE, SLOT_TYPE_CARGO } from '@/config/scenarioConfig'

// 連続マーク間の最小距離 (m)。fork_webapp_react の cargoMarkUtil と同値。
export const NORMAL_MIN_DISTANCE = 0.3
// 荷役（高さ保存）マークに隣接する区間の最小距離 (m)
export const CARGO_MIN_DISTANCE = 1.6

export type MarkValidationError =
  | {
      // エラー区間の終端マークのインデックス（前のマークとの間の区間が対象）
      index: number
      kind: 'tooClose' | 'cargoTooClose'
      distance: number
      minDistance: number
    }
  | {
      // 中間マークに荷役（高さ保存）が設定されている（先頭 / 末尾のみ許可）
      index: number
      kind: 'cargoNotEndpoint'
    }
  | {
      // 荷役（高さ保存）マークと走行方向の不整合。
      // cargoStartNotForward: 先頭の荷役マークからの出発が後進になっている
      // cargoEndNotReverse: 末尾の荷役マークへの進入が前進になっている
      // cargoNeedsIntermediate: 両端荷役の2マークルート（前進出発と後進進入を両立できない）
      index: number
      kind:
        | 'cargoStartNotForward'
        | 'cargoEndNotReverse'
        | 'cargoNeedsIntermediate'
    }

// getMap のマップから指定ルート (mapId) のマーク列を復元する。
// ステーション rec-{i}-{j} をマーク番号 i でグループ化し、
// j = i-1 のステーション theta（前のマークから入る向き）と区間方向の内積で
// 前進/後進を判定する。復元できない場合は null。
export function extractEditableMarks(
  map: RouteMap,
  mapId: string
): EditableMark[] | null {
  const prefix = `*r${mapId}/rec-`
  const groups = new Map<number, { x: number; y: number; inTheta?: number }>()

  for (const st of map.stations.values()) {
    if (!st.id.startsWith(prefix) || !st.pose) continue
    const nums = st.id.slice(prefix.length).split('-')
    if (nums.length !== 2) continue
    const i = Number(nums[0])
    const j = Number(nums[1])
    if (!Number.isInteger(i) || !Number.isInteger(j)) continue
    const g = groups.get(i) ?? { x: st.pose.x, y: st.pose.y }
    g.x = st.pose.x
    g.y = st.pose.y
    if (j === i - 1) g.inTheta = st.pose.theta
    groups.set(i, g)
  }

  if (groups.size < 2) return null
  const count = Math.max(...groups.keys()) + 1

  const marks: EditableMark[] = []
  for (let i = 0; i < count; i++) {
    const g = groups.get(i)
    // マーク番号に欠番がある場合は復元不能とみなす
    if (!g) return null
    marks.push({ x: g.x, y: g.y, reverse: false, isCargo: false, height: -1 })
  }

  for (let i = 1; i < count; i++) {
    const inTheta = groups.get(i)!.inTheta
    if (inTheta == null) continue
    const dx = marks[i].x - marks[i - 1].x
    const dy = marks[i].y - marks[i - 1].y
    marks[i].reverse = Math.cos(inTheta) * dx + Math.sin(inTheta) * dy < 0
  }

  // 端点の荷役設定（rec-1 = 先頭マーク / rec-2 = 末尾マーク）。
  // 旧形式の slots.csv は type 列が無く荷役扱いで読まれるため、height >= 0 も条件にする。
  const applySlot = (slotName: string, markIndex: number) => {
    const slot = map.slots.get(`*r${mapId}/${slotName}`)
    if (!slot) return
    const isCargo = slot.type === SLOT_TYPE_CARGO && slot.height >= 0
    marks[markIndex].isCargo = isCargo
    marks[markIndex].height = isCargo ? slot.height : -1
  }
  applySlot('rec-1', 0)
  applySlot('rec-2', count - 1)

  return marks
}

// 各マークの theta（送信値 / 表示用の機体向き）を区間方向から算出する。
// マーク i (i>=1) の theta = 前のマークからの区間方向（後進は +π）。
// 先頭マークの theta は機体側で使われないため、次の区間の向きに合わせる。
export function computeMarkThetas(marks: EditableMark[]): number[] {
  // 1点のみの間は仮の向き（draftTheta）を表示に使う
  if (marks.length === 1) return [marks[0].draftTheta ?? 0]
  const thetas = marks.map(() => 0)
  for (let i = 1; i < marks.length; i++) {
    const dx = marks[i].x - marks[i - 1].x
    const dy = marks[i].y - marks[i - 1].y
    let theta = Math.atan2(dy, dx)
    if (marks[i].reverse) theta = wrapAngle(theta + Math.PI)
    thetas[i] = theta
  }
  if (marks.length >= 2) thetas[0] = thetas[1]
  return thetas
}

// mark_at_poses の引数配列（1 マークにつき type, x, y, theta, height, slotType の 6 要素）。
// fork_webapp_react の buildMarkAtPosesArgs と同じ規約。
export function buildMarkAtPosesArgs(marks: EditableMark[]): string[] {
  const thetas = computeMarkThetas(marks)
  const args: string[] = []
  marks.forEach((m, i) => {
    const asCargo = m.isCargo || !ENABLE_CARGO_TOGGLE
    args.push(
      'work',
      String(m.x),
      String(m.y),
      String(thetas[i]),
      asCargo ? String(Math.max(m.height, 0)) : '-1',
      asCargo ? '1' : '0'
    )
  })
  return args
}

// マーク列の検証。
// - 区間距離が最小距離（荷役隣接は 1.6m / 通常 0.3m）未満ならエラー
// - 荷役（高さ保存）マークが先頭 / 末尾以外にあればエラー
//   （fork_webapp_react の記録時と同じ制約。機体側も端点スロット (rec-1/rec-2)
//   の荷役情報しか保存しない）
export function validateMarks(marks: EditableMark[]): MarkValidationError[] {
  const errors: MarkValidationError[] = []
  if (ENABLE_CARGO_TOGGLE) {
    for (let i = 1; i < marks.length - 1; i++) {
      if (marks[i].isCargo) {
        errors.push({ index: i, kind: 'cargoNotEndpoint' })
      }
    }
  }
  // 荷役（高さ保存）マークの走行方向の検証。
  // 実記録データの規約: 荷役マークからの出発は前進、荷役マークへの進入は後進
  // （記録時は隣接マークが荷役マークの theta 前方の直線上に補正されるため、
  // パレットから前進で離れ / パレットへ後進（爪側）で進入する経路になる）。
  if (ENABLE_CARGO_TOGGLE && marks.length >= 2) {
    const last = marks.length - 1
    if (marks.length === 2 && marks[0].isCargo && marks[last].isCargo) {
      // 前進出発と後進進入は1区間では両立できないため中間マークが必要
      errors.push({ index: last, kind: 'cargoNeedsIntermediate' })
    } else {
      if (marks[0].isCargo && marks[1].reverse) {
        errors.push({ index: 0, kind: 'cargoStartNotForward' })
      }
      if (marks[last].isCargo && !marks[last].reverse) {
        errors.push({ index: last, kind: 'cargoEndNotReverse' })
      }
    }
  }
  for (let i = 1; i < marks.length; i++) {
    const dx = marks[i].x - marks[i - 1].x
    const dy = marks[i].y - marks[i - 1].y
    const distance = Math.sqrt(dx * dx + dy * dy)
    const isCargoAdjacent = marks[i - 1].isCargo || marks[i].isCargo
    const minDistance =
      ENABLE_CARGO_TOGGLE && isCargoAdjacent
        ? CARGO_MIN_DISTANCE
        : NORMAL_MIN_DISTANCE
    if (distance < minDistance) {
      errors.push({
        index: i,
        kind:
          isCargoAdjacent && ENABLE_CARGO_TOGGLE ? 'cargoTooClose' : 'tooClose',
        distance,
        minDistance,
      })
    }
  }
  return errors
}

// 端点マーク（先頭 / 末尾）の向き (theta) を変更したマーク列を返す。
// マークの向きは隣接区間の方向から導出される（機体側で必ず正規化される）ため、
// 隣のマークを端点を中心に回転させて区間方向を合わせる（区間長は保持）。
// 1点のみの場合は仮の向き（draftTheta）として保持し、2点目配置時の
// 出発方向あわせに使う。端点以外は変更できず null を返す。
export function rotateEndpointTo(
  marks: EditableMark[],
  index: number,
  theta: number
): EditableMark[] | null {
  const n = marks.length
  if (n === 1 && index === 0) {
    return [{ ...marks[0], draftTheta: wrapAngle(theta) }]
  }
  if (n < 2) return null
  if (index === 0) {
    // 先頭マークの向き = 最初の区間の走行向き（後進なら区間方向の逆）
    const dist = Math.hypot(marks[1].x - marks[0].x, marks[1].y - marks[0].y)
    const dir = marks[1].reverse ? theta + Math.PI : theta
    const next = marks.slice()
    next[1] = {
      ...marks[1],
      x: marks[0].x + dist * Math.cos(dir),
      y: marks[0].y + dist * Math.sin(dir),
    }
    return next
  }
  if (index === n - 1) {
    const dist = Math.hypot(
      marks[n - 1].x - marks[n - 2].x,
      marks[n - 1].y - marks[n - 2].y
    )
    const dir = marks[n - 1].reverse ? theta + Math.PI : theta
    const next = marks.slice()
    next[n - 2] = {
      ...marks[n - 2],
      x: marks[n - 1].x - dist * Math.cos(dir),
      y: marks[n - 1].y - dist * Math.sin(dir),
    }
    return next
  }
  return null
}

// 吸着時の角度あわせで許容する、端点の現在の向きとスロット向きのずれの上限。
// これを超えるずれは「ユーザーが別方向へ経路を描いている」とみなして
// あわせない（回転で隣のマークが大きく飛ぶのを防ぐ）。
export const SNAP_ALIGN_MAX_DIFF = Math.PI / 4

// 端点マークの向きを吸着先スロットの向きへあわせる（rotateEndpointTo の吸着用
// ラッパー）。現在の端点の向き（区間方向から導出、後進は +π）とスロット向きの
// ずれが maxDiff を超える場合はあわせずに null を返す（位置吸着のみにする）。
export function alignEndpointToSlot(
  marks: EditableMark[],
  index: number,
  slotTheta: number,
  maxDiff: number = SNAP_ALIGN_MAX_DIFF
): EditableMark[] | null {
  const n = marks.length
  if (n < 2 || (index !== 0 && index !== n - 1)) return null
  const facing = computeMarkThetas(marks)[index]
  if (Math.abs(wrapAngle(facing - slotTheta)) > maxDiff) return null
  return rotateEndpointTo(marks, index, slotTheta)
}

// 指定座標に最も近い区間（マーク i → i+1）のインデックス i を返す（マーク挿入用）。
export function nearestSegmentIndex(
  marks: EditableMark[],
  pos: { x: number; y: number }
): number {
  let best = 0
  let bestDistSq = Infinity
  for (let i = 0; i + 1 < marks.length; i++) {
    const d = distToSegmentSq(pos, marks[i], marks[i + 1])
    if (d < bestDistSq) {
      bestDistSq = d
      best = i
    }
  }
  return best
}

function distToSegmentSq(
  p: { x: number; y: number },
  a: { x: number; y: number },
  b: { x: number; y: number }
): number {
  const dx = b.x - a.x
  const dy = b.y - a.y
  const lenSq = dx * dx + dy * dy
  let t = lenSq === 0 ? 0 : ((p.x - a.x) * dx + (p.y - a.y) * dy) / lenSq
  t = Math.max(0, Math.min(1, t))
  const cx = a.x + t * dx
  const cy = a.y + t * dy
  return (p.x - cx) ** 2 + (p.y - cy) ** 2
}

function wrapAngle(theta: number): number {
  let t = theta
  while (t > Math.PI) t -= 2 * Math.PI
  while (t < -Math.PI) t += 2 * Math.PI
  return t
}
